import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { useLanguage } from './LanguageContext';
import { Brain, Database, Layers, RefreshCw } from 'lucide-react';
interface SlidePlatformArchitectureProps {
  onContinue: () => void;
}
export function SlidePlatformArchitecture({
  onContinue
}: SlidePlatformArchitectureProps) {
  const {
    t
  } = useLanguage();
  const [stage, setStage] = useState(0);
  const layers = [{
    id: 'app',
    title: t('Application Suite', 'Applikationspakke'),
    color: 'bg-teal-400',
    textColor: 'text-teal-400',
    items: [t('Snak Composer', 'Snak Composer'), t('Snakker Player', 'Snakker Player'), t('Distribution', 'Distribution'), t('Analytics', 'Analyse')]
  }, {
    id: 'ai',
    title: t('AI Intelligence Engine', 'AI-intelligensmotor'),
    color: 'bg-purple-500',
    textColor: 'text-purple-400',
    items: [t('Blueprint Analysis', 'Blueprint-analyse'), t('Story Generation', 'Historiegenerering'), t('Content Creation', 'Indholdsskabelse'), t('Channel Optimization', 'Kanaloptimering')]
  }, {
    id: 'data',
    title: t('Data Supply Chain', 'Data-forsyningskæde'),
    color: 'bg-rose-500',
    textColor: 'text-rose-400',
    items: [t('TA Themes', 'TA-temaer'), t('Brand Data', 'Brand-data'), t('HCP Insights', 'HCP-indsigter'), t('Market Intel', 'Markedsindsigt')]
  }];
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const t2 = setTimeout(() => setStage(2), 800);
    const timers = layers.map((_, idx) => setTimeout(() => setStage(3 + idx), 1300 + idx * 500));
    const final = setTimeout(() => setStage(3 + layers.length), 1300 + layers.length * 500 + 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      timers.forEach(clearTimeout);
      clearTimeout(final);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-8 md:px-20 md:py-10 max-w-7xl mx-auto" data-id="element-690">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center space-y-4" data-id="element-691">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-692">
          {t('Platform Architecture', 'Platformarkitektur')}
        </h2>
        <p className="text-xl text-white/60" data-id="element-693">
          {t('An AI-driven closed-loop system.', 'Et AI-drevet lukket system.')}
        </p>
      </motion.div>

      {/* Subheader chip */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 2 ? 1 : 0
    }} className="flex items-center gap-4 mt-5" data-id="element-694">
        <span className="px-3 py-1 text-xs uppercase tracking-widest text-teal-300 bg-teal-400/10 border border-teal-400/30 rounded-full font-semibold" data-id="element-695">
          Platform Architecture
        </span>
        <div className="flex items-center gap-2 text-white/40 text-sm italic" data-id="element-696">
          <RefreshCw className="w-3.5 h-3.5" data-id="element-697" />
          Think · Create · Learn · Improve
        </div>
      </motion.div>

      {/* Layered stack */}
      <div className="flex-1 flex flex-col gap-3 mt-5" data-id="element-698">
        {layers.map((layer, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: stage >= 3 + idx ? 1 : 0,
        x: stage >= 3 + idx ? 0 : -20
      }} transition={{
        duration: 0.5
      }} className="bg-navy-900/60 border border-teal-400/20 rounded-2xl p-5 md:p-6" data-id="element-699">
            <div className="flex items-start gap-4 mb-3" data-id="element-700">
              <div className="w-10 h-10 rounded-xl bg-teal-400/10 border border-teal-400/30 flex items-center justify-center text-teal-400 flex-shrink-0" data-id="element-701">
                {layer.icon}
              </div>
              <div className="flex-1 min-w-0" data-id="element-702">
                <h3 className="text-lg md:text-xl font-bold text-teal-300" data-id="element-703">
                  {layer.title}
                  {layer.titleSuffix && <span className="text-white/50 font-medium text-base ml-2" data-id="element-704">
                      {layer.titleSuffix}
                    </span>}
                </h3>
                <p className="text-white/60 text-sm mt-0.5" data-id="element-705">{layer.desc}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3" data-id="element-706">
              {layer.chips.map(chip => <div key={chip} className="bg-navy-800/80 border border-navy-600 rounded-lg px-3 py-2.5 text-center" data-id="element-707">
                  <span className="text-white/85 text-xs md:text-sm font-semibold" data-id="element-708">
                    {chip}
                  </span>
                </div>)}
            </div>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 3 + layers.length ? 1 : 0
    }} className="mt-5 flex justify-end" data-id="element-709">
        <Button onClick={onContinue} data-id="element-710">
          {t('The Business', 'Forretningen')}
        </Button>
      </motion.div>
    </div>;
}